// Canonical section arrays for the ProjectDetailPage render pipeline (R2).
// CORE_SECTIONS are always rendered; ADDON_SECTIONS are toggleable add-ins.
// Order defines the global canonical render order — category/project overrides
// cannot reorder sections; they only affect visibility.

export const CORE_SECTIONS = [
  'project-overview',
  'scopes',
  'billable-items',
  'invoices',
  'permits',
  'documents',
  'client-actions',
] as const;

export const ADDON_SECTIONS = [
  'deliverables',
  'submittals',
  'rfis',
  'sis',
  'sov',
  'site-visits',
  'portal',
  'messages',
  'team',
] as const;

export type CoreSection = (typeof CORE_SECTIONS)[number];
export type AddonSection = (typeof ADDON_SECTIONS)[number];
export type SectionKey = CoreSection | AddonSection;

/**
 * Feature flag that hard-gates each add-in section.
 * If useFeature(flag) returns false, the section is hidden regardless of
 * all soft-default layers and per-project overrides.
 */
export const SECTION_HARD_GATES: Partial<Record<AddonSection, string>> = {
  portal: 'portal_management',
};

/**
 * Firm-type-level section visibility map (Layer 2 in gate-precedence).
 * In v1, no firm type restricts any add-in by default — extend when
 * firm-type-specific defaults are required.
 */
export const FIRM_TYPE_SECTION_VISIBILITY: Record<string, Partial<Record<AddonSection, boolean>>> =
  {};

/**
 * Returns the section visibility defaults for a given firm type.
 * Exported as a function so tests can vi.fn() mock it.
 */
export function getFirmTypeVisibility(
  firmType: string,
): Partial<Record<AddonSection, boolean>> {
  return FIRM_TYPE_SECTION_VISIBILITY[firmType] ?? {};
}

/**
 * Friendly label and one-line description for each add-in section.
 * Used by the module picker modal (R3) to list hidden add-ins.
 * Core sections are included for label lookup only.
 */
export const SECTION_METADATA: Record<string, { label: string; description: string }> = {
  deliverables: { label: 'Deliverables', description: 'Drawings, reports, and other deliverable artifacts.' },
  submittals: { label: 'Submittals', description: 'Construction submittal tracking and review.' },
  rfis: { label: 'RFIs', description: 'Requests for information between project parties.' },
  sis: { label: 'Supplemental Instructions', description: 'Architect-issued supplemental instructions to the contractor.' },
  sov: { label: 'Schedule of Values', description: 'Breakdown of contract value across work items for progress billing.' },
  'site-visits': { label: 'Site Visits', description: 'Field observation reports and site visit logs.' },
  portal: { label: 'Client Portal', description: 'Shared portal for client access to project documents and updates.' },
  messages: { label: 'Messages', description: 'Project-level message thread with team members.' },
  team: { label: 'Team', description: 'Project team members and their assigned roles.' },
};
